import React from 'react'
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Restaurant,
  Coffee,
  Fastfood,
  Hotel,
  Kitchen,
  FoodBank,
} from "@mui/icons-material";
import { Link } from 'react-router-dom';

const TrustedBy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const businessTypes = [
    {
      icon: <Restaurant sx={{ fontSize: { xs: 32, md: 40 } }} />,
      label: "Restaurants",
    },
    {
      icon: <Coffee sx={{ fontSize: { xs: 32, md: 40 } }} />,
      label: "Cafes",
    },
    {
      icon: <Fastfood sx={{ fontSize: { xs: 32, md: 40 } }} />,
      label: "Fast Food",
    },
    {
      icon: <Hotel sx={{ fontSize: { xs: 32, md: 40 } }} />,
      label: "Hotels",
    },
    {
      icon: <Kitchen sx={{ fontSize: { xs: 32, md: 40 } }} />,
      label: "Cloud Kitchen",
    },
    {
      icon: <FoodBank sx={{ fontSize: { xs: 32, md: 40 } }} />,
      label: "Food Court",
    }
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        bgcolor: "var(--bg-light)",
        borderTop: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        
        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              color: "var(--primary)",
              fontWeight: 700,
              fontSize: { xs: "13px", sm: "14px", md: "14px" },
              letterSpacing: "2px",
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 2,
                bgcolor: "var(--primary)",
                borderRadius: "2px",
              }}
            />
            Perfect For
            <Box
              sx={{
                width: 30,
                height: 2,
                bgcolor: "var(--primary)",
                borderRadius: "2px",
              }}
            />
          </Typography>

          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2rem", sm: "2.8rem", md: "2.8rem" },
              color: "var(--text)",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            Trusted By{" "}
            <Box component="span" sx={{ color: "var(--primary-dark)" }}>
              Food Businesses
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "var(--text-light)",
              fontSize: { xs: "15px", sm: "16px", md: "16px" },
              maxWidth: "600px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Our platform is designed for every type of food business
          </Typography>
        </Box>

        {/* Business Types Grid */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
          {businessTypes?.map((item, index) => (
            <Grid item size={{xs:6, sm:4, md:2 }} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3, md: 4 },
                  borderRadius: "20px",
                  textAlign: "center",
                  bgcolor: "#ffffff",
                  border: "1px solid var(--border)",
                  transition: "all 0.3s ease",
                  cursor: "default",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: { xs: 120, sm: 140, md: 160 },
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "var(--shadow-orange)",
                    borderColor: "var(--primary)",
                    bgcolor: "var(--bg-orange)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 56, sm: 64, md: 72 },
                    height: { xs: 56, sm: 64, md: 72 },
                    borderRadius: "16px",
                    bgcolor: "var(--bg-primary-soft)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--primary)",
                    mb: { xs: 1.5, md: 2 },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "rgba(255, 107, 0, 0.15)",
                      transform: "scale(1.05) rotate(-3deg)",
                    },
                  }}
                >
                  {item.icon}
                </Box>
                
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "14px", sm: "15px", md: "16px" },
                    color: "var(--text)",
                    lineHeight: 1.3,
                  }}
                >
                  {item.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Stats or Additional Info */}
        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 2, sm: 4 },
            p: { xs: 3, sm: 4, md: 5 },
            bgcolor: "#ffffff",
            borderRadius: "20px",
            border: "1px solid var(--border)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: "var(--primary)",
                borderRadius: "50%",
                display: "inline-block",
              }}
            />
            <Typography sx={{ color: "var(--text-light)", fontSize: { xs: "14px", md: "15px" } }}>
              No clients yet?{" "}
              <Box component="span" sx={{ color: "var(--primary-dark)", fontWeight: 700 }}>
                Perfect for
              </Box>{" "}
              all food businesses
            </Typography>
          </Box>
          
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              bgcolor: "var(--bg-primary-soft)",
              borderRadius: "30px",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                bgcolor: "var(--primary)",
                borderRadius: "50%",
                animation: "pulse 2s infinite",
              }}
            />
            <Typography sx={{ color: "var(--primary)", fontWeight: 600, fontSize: { xs: "13px", md: "14px" } }}>
            <Link to="/register" 
            style={{
              color:"inherit",
              textDecoration:"none"
            }}>
              Start growing today!</Link>  
            </Typography>
          </Box>
        </Box>

        {/* Animation keyframes */}
        <style>
          {`
            @keyframes pulse {
              0% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.5;
                transform: scale(1.2);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}
        </style>
      </Container>
    </Box>
  );
};

export default TrustedBy;